import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAppointmentProviders,
  qInventoryPickerCandidates,
  qPatientById,
  qPrescriptionPrintSettings,
  qPrescriptionsByEncounter,
  qUserProviderLink,
  pickerCandidateRowToSelection,
  type AppConfigRow,
  type AppointmentProviderRow,
  type InventoryPickerCandidateRow,
  type InventoryPickerSelection,
  type PatientDetailRow,
  type PatientPrescriptionRow,
  type PrescriptionPrintSettingsRow,
  type UserProviderLinkRow,
} from '@helios/data-access';
import {
  PrescriptionPrintLayout,
  type PrintPrescriptionItem,
} from '../../clinic/prescription-print/PrescriptionPrintLayout';
import {
  PRINT_STYLES,
  formatPatientAge,
  resolveDoctor,
  resolvePrintSettings,
} from '../../clinic/prescription-print/print-utils';

// ─── Constants ───

const INTAKE_TIMING_OPTIONS = [
  { value: '', label: 'Select timing…' },
  { value: 'before_food', label: 'Before Food' },
  { value: 'after_food', label: 'After Food' },
  { value: 'with_food', label: 'With Food' },
  { value: 'empty_stomach', label: 'Empty Stomach' },
  { value: 'anytime', label: 'Anytime' },
] as const;

const DURATION_UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
] as const;

// ─── Draft (in-memory editable row) ───

interface PrescriptionDraft {
  id: string;
  isNew: boolean;
  itemName: string;
  itemDetails: string;
  intakeTiming: string;
  doseQuantity: string;
  scheduleMorning: boolean;
  scheduleAfternoon: boolean;
  scheduleNight: boolean;
  durationValue: string; // input as string for empty-state ergonomics
  durationUnit: string;
  instructions: string;
  sortOrder: number;

  // PRD.189 — optional Medicine inventory reference + snapshots.
  // When inventoryItemId is set, snapshot fields hold the catalog values
  // captured at the time of selection. Free-text-only lines leave
  // inventoryItemId null and may set freeTextName.
  inventoryItemId: string | null;
  freeTextName: string | null;
  itemNameSnapshot: string | null;
  skuSnapshot: string | null;
  genericNameSnapshot: string | null;
  brandNameSnapshot: string | null;
  strengthSnapshot: string | null;
  dosageFormSnapshot: string | null;
  routeSnapshot: string | null;
  unitSnapshot: string | null;
  defaultInstructionSnapshot: string | null;
  defaultDosageSnapshot: string | null;
  usageSnapshot: string | null;
  doseSnapshot: string | null;
  doseUnitSnapshot: string | null;
  frequencySnapshot: string | null;
  durationSnapshot: string | null;
}

function rowToDraft(row: PatientPrescriptionRow): PrescriptionDraft {
  return {
    id: row.id,
    isNew: false,
    itemName: row.item_name ?? '',
    itemDetails: row.item_details ?? '',
    intakeTiming: row.intake_timing ?? '',
    doseQuantity: row.dose_quantity ?? '',
    scheduleMorning: row.schedule_morning === 1,
    scheduleAfternoon: row.schedule_afternoon === 1,
    scheduleNight: row.schedule_night === 1,
    durationValue: row.duration_value != null ? String(row.duration_value) : '',
    durationUnit: row.duration_unit ?? 'days',
    instructions: row.instructions ?? '',
    sortOrder: row.sort_order ?? 0,
    inventoryItemId: row.inventory_item_id,
    freeTextName: row.free_text_name,
    itemNameSnapshot: row.item_name_snapshot,
    skuSnapshot: row.sku_snapshot,
    genericNameSnapshot: row.generic_name_snapshot,
    brandNameSnapshot: row.brand_name_snapshot,
    strengthSnapshot: row.strength_snapshot,
    dosageFormSnapshot: row.dosage_form_snapshot,
    routeSnapshot: row.route_snapshot,
    unitSnapshot: row.unit_snapshot,
    defaultInstructionSnapshot: row.default_instruction_snapshot,
    defaultDosageSnapshot: row.default_dosage_snapshot,
    usageSnapshot: row.usage_snapshot,
    doseSnapshot: row.dose_snapshot,
    doseUnitSnapshot: row.dose_unit_snapshot,
    frequencySnapshot: row.frequency_snapshot,
    durationSnapshot: row.duration_snapshot,
  };
}

function emptyDraft(sortOrder: number): PrescriptionDraft {
  return {
    id: crypto.randomUUID(),
    isNew: true,
    itemName: '',
    itemDetails: '',
    intakeTiming: '',
    doseQuantity: '',
    scheduleMorning: false,
    scheduleAfternoon: false,
    scheduleNight: false,
    durationValue: '',
    durationUnit: 'days',
    instructions: '',
    sortOrder,
    inventoryItemId: null,
    freeTextName: null,
    itemNameSnapshot: null,
    skuSnapshot: null,
    genericNameSnapshot: null,
    brandNameSnapshot: null,
    strengthSnapshot: null,
    dosageFormSnapshot: null,
    routeSnapshot: null,
    unitSnapshot: null,
    defaultInstructionSnapshot: null,
    defaultDosageSnapshot: null,
    usageSnapshot: null,
    doseSnapshot: null,
    doseUnitSnapshot: null,
    frequencySnapshot: null,
    durationSnapshot: null,
  };
}

// Build "Brand · Generic · Strength · Form" from a picker selection for the
// item-details column. Falls back to '' if all fields are blank.
function buildItemDetailsFromSelection(s: InventoryPickerSelection): string {
  return [s.brandName, s.genericName, s.strength, s.dosageForm].filter(Boolean).join(' · ');
}

// Apply a picker selection onto a draft: bind the inventory item, populate
// snapshot fields, and prefill display/dosage/instruction fields the
// prescriber may still edit. Existing prescriber edits are preserved when
// the corresponding inventory field is empty.
function applyPickerSelection(
  draft: PrescriptionDraft,
  selection: InventoryPickerSelection,
): PrescriptionDraft {
  const next: PrescriptionDraft = {
    ...draft,
    inventoryItemId: selection.inventoryItemId,
    freeTextName: null,
    // Prefill display fields (overwrite — picking an item is an explicit
    // action). The prescriber can still edit each cell after selection.
    itemName: selection.itemName,
    itemDetails: buildItemDetailsFromSelection(selection),
    // Snapshot all candidate fields
    itemNameSnapshot: selection.itemName,
    skuSnapshot: selection.sku,
    genericNameSnapshot: selection.genericName,
    brandNameSnapshot: selection.brandName,
    strengthSnapshot: selection.strength,
    dosageFormSnapshot: selection.dosageForm,
    routeSnapshot: selection.route,
    unitSnapshot: selection.inventoryUnit,
    defaultInstructionSnapshot: selection.defaultInstruction,
    defaultDosageSnapshot: selection.defaultDosage,
    usageSnapshot: selection.usage,
    doseSnapshot: selection.dose,
    doseUnitSnapshot: selection.doseUnit,
    frequencySnapshot: selection.frequency,
    durationSnapshot: selection.duration,
  };
  // Prefill dosage / instructions only when the prescriber hasn't typed
  // anything yet (avoid stomping in-progress edits).
  if (!draft.doseQuantity.trim() && selection.defaultDosage) {
    next.doseQuantity = selection.defaultDosage;
  }
  if (!draft.instructions.trim() && selection.defaultInstruction) {
    next.instructions = selection.defaultInstruction;
  }
  return next;
}

// Free-text mode: clear inventory binding + snapshots; preserve typed text.
function clearInventoryBinding(draft: PrescriptionDraft): PrescriptionDraft {
  return {
    ...draft,
    inventoryItemId: null,
    freeTextName: draft.itemName.trim() || null,
    itemNameSnapshot: null,
    skuSnapshot: null,
    genericNameSnapshot: null,
    brandNameSnapshot: null,
    strengthSnapshot: null,
    dosageFormSnapshot: null,
    routeSnapshot: null,
    unitSnapshot: null,
    defaultInstructionSnapshot: null,
    defaultDosageSnapshot: null,
    usageSnapshot: null,
    doseSnapshot: null,
    doseUnitSnapshot: null,
    frequencySnapshot: null,
    durationSnapshot: null,
  };
}

// ─── Props ───

interface EncounterPrescriptionsTabProps {
  patientId: string;
  encounterId: string;
  visitId?: string | null;
  hideHeader?: boolean;
  quietActions?: boolean;
}

// ─── Component ───

export function EncounterPrescriptionsTab({
  patientId,
  encounterId,
  visitId,
  hideHeader,
  quietActions,
}: EncounterPrescriptionsTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  const { data: rows = [] } = usePowerSyncQuery<PatientPrescriptionRow>(
    qPrescriptionsByEncounter(encounterId),
  );

  // ── Print Prescription (PRD.184) ──
  // The action uses the clinic's saved org-default print settings, falling
  // back to app_config branding when the wizard hasn't been run yet, and
  // resolves the printing user's doctor identity from their linked provider.
  const { data: printSettingsRows = [] } = usePowerSyncQuery<PrescriptionPrintSettingsRow>(
    qPrescriptionPrintSettings(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const { data: providerLinkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgId, userId),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));

  const printSettings = useMemo(
    () => resolvePrintSettings(printSettingsRows[0] ?? null, appConfigRows[0] ?? null),
    [printSettingsRows, appConfigRows],
  );
  const printDoctor = useMemo(() => {
    const linkedProviderId = providerLinkRows[0]?.provider_id ?? null;
    const linkedProvider = linkedProviderId
      ? (providerRows.find((p) => p.id === linkedProviderId) ?? null)
      : null;
    return resolveDoctor(linkedProvider, user?.displayName ?? null);
  }, [providerLinkRows, providerRows, user?.displayName]);

  const printPatient = useMemo(() => {
    const p = patientRows[0];
    return {
      name: p?.full_name ?? '',
      age: formatPatientAge(p?.birth_date),
      sex: p?.sex ?? '',
    };
  }, [patientRows]);

  const printItems: PrintPrescriptionItem[] = useMemo(
    () =>
      // PRD.189 — prefer snapshot fields when present so historical
      // prescriptions render correctly even if the inventory item
      // was edited or deactivated later.
      rows.map((r) => {
        const snapshotDetails = [
          r.brand_name_snapshot,
          r.generic_name_snapshot,
          r.strength_snapshot,
          r.dosage_form_snapshot,
        ]
          .filter(Boolean)
          .join(' · ');
        return {
          itemName: r.item_name_snapshot ?? r.free_text_name ?? r.item_name ?? 'Untitled medicine',
          itemDetails: r.item_details ?? snapshotDetails,
          intakeTiming: r.intake_timing ?? '',
          doseQuantity: r.dose_quantity ?? r.default_dosage_snapshot ?? '',
          scheduleMorning: r.schedule_morning === 1,
          scheduleAfternoon: r.schedule_afternoon === 1,
          scheduleNight: r.schedule_night === 1,
          durationValue: r.duration_value ?? null,
          durationUnit: r.duration_unit ?? '',
          instructions: r.instructions ?? r.default_instruction_snapshot ?? '',
        };
      }),
    [rows],
  );

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Drafts for newly added rows that haven't been saved yet (no DB row exists).
  const [pendingDrafts, setPendingDrafts] = useState<PrescriptionDraft[]>([]);
  const [editingDrafts, setEditingDrafts] = useState<Record<string, PrescriptionDraft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const savedDrafts: PrescriptionDraft[] = useMemo(
    () => rows.map((r) => editingDrafts[r.id] ?? rowToDraft(r)),
    [rows, editingDrafts],
  );

  const allDrafts: PrescriptionDraft[] = useMemo(
    () => [...savedDrafts, ...pendingDrafts],
    [savedDrafts, pendingDrafts],
  );

  // Detect dirty state for saved rows (so we know when to show Save button)
  const isRowDirty = useCallback(
    (draft: PrescriptionDraft): boolean => {
      if (draft.isNew) return true;
      const existing = rows.find((r) => r.id === draft.id);
      if (!existing) return true;
      const persisted = rowToDraft(existing);
      return JSON.stringify(persisted) !== JSON.stringify(draft);
    },
    [rows],
  );

  const updateDraft = useCallback(
    (id: string, patch: Partial<PrescriptionDraft>) => {
      // Pending (unsaved) row?
      const pendingIdx = pendingDrafts.findIndex((d) => d.id === id);
      if (pendingIdx >= 0) {
        setPendingDrafts((prev) => prev.map((d, i) => (i === pendingIdx ? { ...d, ...patch } : d)));
        return;
      }
      // Saved row — track edits in editingDrafts
      const existing = rows.find((r) => r.id === id);
      if (!existing) return;
      const base = editingDrafts[id] ?? rowToDraft(existing);
      setEditingDrafts((prev) => ({ ...prev, [id]: { ...base, ...patch } }));
    },
    [pendingDrafts, rows, editingDrafts],
  );

  const handleAddRow = useCallback(() => {
    const sortOrder = allDrafts.length;
    setPendingDrafts((prev) => [...prev, emptyDraft(sortOrder)]);
  }, [allDrafts.length]);

  const handleSave = useCallback(
    async (draft: PrescriptionDraft) => {
      if (!db) return;
      if (!draft.itemName.trim()) return;
      setSavingId(draft.id);
      try {
        const now = new Date().toISOString();
        const durationValue = draft.durationValue.trim() ? parseInt(draft.durationValue, 10) : null;

        // PRD.189 — derive free_text_name when no inventory item is bound.
        // Always also write the legacy item_name (NOT NULL) for backward
        // compatibility and to keep existing display paths working.
        const freeTextName = draft.inventoryItemId ? null : draft.itemName.trim() || null;

        if (draft.isNew) {
          await db.execute(
            `INSERT INTO patient_prescriptions
              (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
               item_name, item_details,
               inventory_item_id, free_text_name,
               item_name_snapshot, sku_snapshot, generic_name_snapshot, brand_name_snapshot,
               strength_snapshot, dosage_form_snapshot, route_snapshot, unit_snapshot,
               default_instruction_snapshot, default_dosage_snapshot, usage_snapshot,
               dose_snapshot, dose_unit_snapshot, frequency_snapshot, duration_snapshot,
               intake_timing, dose_quantity,
               schedule_morning, schedule_afternoon, schedule_night,
               duration_value, duration_unit, instructions, sort_order,
               author_user_id, author_name, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?,
                     ?, ?,
                     ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?,
                     ?, ?, ?,
                     ?, ?, ?, ?,
                     ?, ?, ?, ?)`,
            [
              draft.id,
              orgId,
              null,
              patientId,
              encounterId,
              visitId ?? null,
              draft.itemName.trim(),
              draft.itemDetails.trim() || null,
              draft.inventoryItemId,
              freeTextName,
              draft.itemNameSnapshot,
              draft.skuSnapshot,
              draft.genericNameSnapshot,
              draft.brandNameSnapshot,
              draft.strengthSnapshot,
              draft.dosageFormSnapshot,
              draft.routeSnapshot,
              draft.unitSnapshot,
              draft.defaultInstructionSnapshot,
              draft.defaultDosageSnapshot,
              draft.usageSnapshot,
              draft.doseSnapshot,
              draft.doseUnitSnapshot,
              draft.frequencySnapshot,
              draft.durationSnapshot,
              draft.intakeTiming || null,
              draft.doseQuantity.trim() || null,
              draft.scheduleMorning ? 1 : 0,
              draft.scheduleAfternoon ? 1 : 0,
              draft.scheduleNight ? 1 : 0,
              durationValue != null && !Number.isNaN(durationValue) ? durationValue : null,
              draft.durationUnit || null,
              draft.instructions.trim() || null,
              draft.sortOrder,
              userId,
              userName,
              now,
              now,
            ],
          );
          setPendingDrafts((prev) => prev.filter((d) => d.id !== draft.id));
        } else {
          await db.execute(
            `UPDATE patient_prescriptions
             SET item_name = ?, item_details = ?,
                 inventory_item_id = ?, free_text_name = ?,
                 item_name_snapshot = ?, sku_snapshot = ?, generic_name_snapshot = ?,
                 brand_name_snapshot = ?, strength_snapshot = ?, dosage_form_snapshot = ?,
                 route_snapshot = ?, unit_snapshot = ?,
                 default_instruction_snapshot = ?, default_dosage_snapshot = ?, usage_snapshot = ?,
                 dose_snapshot = ?, dose_unit_snapshot = ?, frequency_snapshot = ?, duration_snapshot = ?,
                 intake_timing = ?, dose_quantity = ?,
                 schedule_morning = ?, schedule_afternoon = ?, schedule_night = ?,
                 duration_value = ?, duration_unit = ?, instructions = ?, sort_order = ?,
                 updated_at = ?
             WHERE id = ?`,
            [
              draft.itemName.trim(),
              draft.itemDetails.trim() || null,
              draft.inventoryItemId,
              freeTextName,
              draft.itemNameSnapshot,
              draft.skuSnapshot,
              draft.genericNameSnapshot,
              draft.brandNameSnapshot,
              draft.strengthSnapshot,
              draft.dosageFormSnapshot,
              draft.routeSnapshot,
              draft.unitSnapshot,
              draft.defaultInstructionSnapshot,
              draft.defaultDosageSnapshot,
              draft.usageSnapshot,
              draft.doseSnapshot,
              draft.doseUnitSnapshot,
              draft.frequencySnapshot,
              draft.durationSnapshot,
              draft.intakeTiming || null,
              draft.doseQuantity.trim() || null,
              draft.scheduleMorning ? 1 : 0,
              draft.scheduleAfternoon ? 1 : 0,
              draft.scheduleNight ? 1 : 0,
              durationValue != null && !Number.isNaN(durationValue) ? durationValue : null,
              draft.durationUnit || null,
              draft.instructions.trim() || null,
              draft.sortOrder,
              now,
              draft.id,
            ],
          );
          setEditingDrafts((prev) => {
            const next = { ...prev };
            delete next[draft.id];
            return next;
          });
        }
      } finally {
        setSavingId(null);
      }
    },
    [db, orgId, patientId, encounterId, visitId, userId, userName],
  );

  const handleRemove = useCallback(
    async (draft: PrescriptionDraft) => {
      if (draft.isNew) {
        setPendingDrafts((prev) => prev.filter((d) => d.id !== draft.id));
        return;
      }
      if (!db) return;
      await db.execute(`DELETE FROM patient_prescriptions WHERE id = ?`, [draft.id]);
      setEditingDrafts((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
    },
    [db],
  );

  const printDisabled = printItems.length === 0;

  return (
    <>
      <style>{PRINT_STYLES}</style>
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!hideHeader && (
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                Prescription
              </h2>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                Items prescribed during this consultation
              </p>
            </div>
          )}
          {hideHeader && <div />}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {/* Print Prescription — uses the saved org-default print layout. */}
            <button
              type="button"
              onClick={handlePrint}
              disabled={printDisabled}
              title={
                printDisabled
                  ? 'Add at least one item before printing'
                  : 'Print using the saved prescription layout'
              }
              style={{
                height: quietActions ? 30 : 36,
                padding: quietActions ? '0 10px' : '0 14px',
                borderRadius: quietActions ? 'var(--radius-sm)' : 'var(--radius-md)',
                border: `1px solid ${printDisabled ? 'var(--border)' : primary}`,
                background: 'var(--bg-surface)',
                color: printDisabled ? 'var(--text-disabled)' : primary,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: printDisabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <NavIcon name="file-text" />
              {!quietActions && 'Print'}
            </button>
            {quietActions ? (
              <button
                onClick={handleAddRow}
                title="Add Item"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                }}
              >
                <NavIcon name="plus" />
              </button>
            ) : (
              <button
                onClick={handleAddRow}
                style={{
                  height: 36,
                  padding: '0 16px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: primary,
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <NavIcon name="plus" /> Add Item
              </button>
            )}
          </div>
        </div>

        {/* Rows */}
        {allDrafts.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--sp-8)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--sp-3)',
            }}
          >
            <span>No prescription items yet.</span>
            <button
              onClick={handleAddRow}
              style={{
                height: 32,
                padding: '0 14px',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${primary}`,
                background: 'transparent',
                color: primary,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <NavIcon name="plus" /> Add Item
            </button>
          </div>
        ) : (
          <PrescriptionGrid
            drafts={allDrafts}
            dirtyIds={new Set(allDrafts.filter((d) => isRowDirty(d)).map((d) => d.id))}
            savingId={savingId}
            primary={primary}
            onChange={updateDraft}
            onSave={handleSave}
            onRemove={handleRemove}
          />
        )}
      </div>

      {/* Print-only mount — rendered to a hidden div on screen. The print
          stylesheet hides the rest of the app and shows just this. */}
      {!printDisabled && (
        <div className="pp-print-only" aria-hidden>
          <PrescriptionPrintLayout
            settings={printSettings}
            doctor={printDoctor}
            patient={printPatient}
            date={new Date().toISOString()}
            items={printItems}
            accentColor={primary}
          />
        </div>
      )}
    </>
  );
}

// ─── Shared-grid table-inspired editor ───

const GRID_TEMPLATE =
  '36px minmax(160px, 1.4fr) minmax(180px, 1.6fr) 130px 90px 132px 154px minmax(180px, 1.6fr) 84px';
const GRID_MIN_WIDTH = 1146;

interface PrescriptionGridProps {
  drafts: PrescriptionDraft[];
  dirtyIds: Set<string>;
  savingId: string | null;
  primary: string;
  onChange: (id: string, patch: Partial<PrescriptionDraft>) => void;
  onSave: (draft: PrescriptionDraft) => void;
  onRemove: (draft: PrescriptionDraft) => void;
}

function PrescriptionGrid({
  drafts,
  dirtyIds,
  savingId,
  primary,
  onChange,
  onSave,
  onRemove,
}: PrescriptionGridProps) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        overflowX: 'auto',
      }}
    >
      <div
        role="table"
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_TEMPLATE,
          columnGap: 0,
          rowGap: 0,
          minWidth: GRID_MIN_WIDTH,
          alignItems: 'center',
        }}
      >
        {/* Header */}
        <HeaderCell align="center">#</HeaderCell>
        <HeaderCell>Item Name</HeaderCell>
        <HeaderCell>Item Details</HeaderCell>
        <HeaderCell>When to take</HeaderCell>
        <HeaderCell>Dose</HeaderCell>
        <HeaderCell>Schedule</HeaderCell>
        <HeaderCell>Duration</HeaderCell>
        <HeaderCell>Instructions</HeaderCell>
        <HeaderCell align="right">Actions</HeaderCell>

        {/* Rows */}
        {drafts.map((draft, idx) => (
          <PrescriptionGridRow
            key={draft.id}
            index={idx}
            draft={draft}
            dirty={dirtyIds.has(draft.id)}
            saving={savingId === draft.id}
            isLast={idx === drafts.length - 1}
            primary={primary}
            onChange={(patch) => onChange(draft.id, patch)}
            onSave={() => onSave(draft)}
            onRemove={() => onRemove(draft)}
          />
        ))}
      </div>
    </div>
  );
}

function HeaderCell({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <div
      style={{
        padding: '10px 8px',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
        textAlign: align,
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      {children}
    </div>
  );
}

// ─── Grid row (cells emitted directly into parent grid) ───

interface PrescriptionGridRowProps {
  index: number;
  draft: PrescriptionDraft;
  dirty: boolean;
  saving: boolean;
  isLast: boolean;
  primary: string;
  onChange: (patch: Partial<PrescriptionDraft>) => void;
  onSave: () => void;
  onRemove: () => void;
}

function PrescriptionGridRow({
  index,
  draft,
  dirty,
  saving,
  isLast,
  primary,
  onChange,
  onSave,
  onRemove,
}: PrescriptionGridRowProps) {
  const canSave = draft.itemName.trim().length > 0 && dirty && !saving;
  const cellStyle: React.CSSProperties = {
    padding: '8px 8px',
    borderBottom: isLast ? 'none' : '1px solid var(--border)',
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
  };
  const inputStyle: React.CSSProperties = {
    height: 32,
    padding: '0 8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <>
      {/* # / Index */}
      <div
        style={{
          ...cellStyle,
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          gap: 4,
        }}
      >
        {index + 1}
        {draft.isNew && (
          <span
            title="Unsaved"
            aria-label="Unsaved"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: primary,
              display: 'inline-block',
            }}
          />
        )}
      </div>

      {/* Item Name (with inventory picker) */}
      <div style={cellStyle}>
        <ItemNamePicker
          value={draft.itemName}
          inventoryItemId={draft.inventoryItemId}
          onTypeFreeText={(text) =>
            // Typing a name in free-text mode leaves any existing
            // inventory binding intact only while the typed text matches
            // the snapshot — diverging text un-binds the inventory item
            // so the snapshot is not silently misrepresenting the line.
            onChange(
              draft.inventoryItemId && text !== draft.itemNameSnapshot
                ? { ...clearInventoryBinding(draft), itemName: text }
                : { itemName: text, freeTextName: draft.inventoryItemId ? null : text || null },
            )
          }
          onSelectInventory={(selection) => {
            const next = applyPickerSelection(draft, selection);
            onChange({
              itemName: next.itemName,
              itemDetails: next.itemDetails,
              doseQuantity: next.doseQuantity,
              instructions: next.instructions,
              inventoryItemId: next.inventoryItemId,
              freeTextName: next.freeTextName,
              itemNameSnapshot: next.itemNameSnapshot,
              skuSnapshot: next.skuSnapshot,
              genericNameSnapshot: next.genericNameSnapshot,
              brandNameSnapshot: next.brandNameSnapshot,
              strengthSnapshot: next.strengthSnapshot,
              dosageFormSnapshot: next.dosageFormSnapshot,
              routeSnapshot: next.routeSnapshot,
              unitSnapshot: next.unitSnapshot,
              defaultInstructionSnapshot: next.defaultInstructionSnapshot,
              defaultDosageSnapshot: next.defaultDosageSnapshot,
              usageSnapshot: next.usageSnapshot,
              doseSnapshot: next.doseSnapshot,
              doseUnitSnapshot: next.doseUnitSnapshot,
              frequencySnapshot: next.frequencySnapshot,
              durationSnapshot: next.durationSnapshot,
            });
          }}
          onClearInventory={() => {
            const next = clearInventoryBinding(draft);
            onChange({
              inventoryItemId: next.inventoryItemId,
              freeTextName: next.freeTextName,
              itemNameSnapshot: next.itemNameSnapshot,
              skuSnapshot: next.skuSnapshot,
              genericNameSnapshot: next.genericNameSnapshot,
              brandNameSnapshot: next.brandNameSnapshot,
              strengthSnapshot: next.strengthSnapshot,
              dosageFormSnapshot: next.dosageFormSnapshot,
              routeSnapshot: next.routeSnapshot,
              unitSnapshot: next.unitSnapshot,
              defaultInstructionSnapshot: next.defaultInstructionSnapshot,
              defaultDosageSnapshot: next.defaultDosageSnapshot,
              usageSnapshot: next.usageSnapshot,
              doseSnapshot: next.doseSnapshot,
              doseUnitSnapshot: next.doseUnitSnapshot,
              frequencySnapshot: next.frequencySnapshot,
              durationSnapshot: next.durationSnapshot,
            });
          }}
          inputStyle={inputStyle}
          primary={primary}
        />
      </div>

      {/* Item Details */}
      <div style={cellStyle}>
        <input
          type="text"
          placeholder="Brand, strength, formulation"
          value={draft.itemDetails}
          onChange={(e) => onChange({ itemDetails: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* When to take */}
      <div style={cellStyle}>
        <select
          value={draft.intakeTiming}
          onChange={(e) => onChange({ intakeTiming: e.target.value })}
          style={inputStyle}
        >
          {INTAKE_TIMING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Dose */}
      <div style={cellStyle}>
        <input
          type="text"
          placeholder="1 tab"
          value={draft.doseQuantity}
          onChange={(e) => onChange({ doseQuantity: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* Schedule (M / A / N) */}
      <div style={cellStyle}>
        <div style={{ display: 'flex', gap: 4 }}>
          <ScheduleChip
            short="M"
            full="Morning"
            active={draft.scheduleMorning}
            primary={primary}
            onClick={() => onChange({ scheduleMorning: !draft.scheduleMorning })}
          />
          <ScheduleChip
            short="A"
            full="Afternoon"
            active={draft.scheduleAfternoon}
            primary={primary}
            onClick={() => onChange({ scheduleAfternoon: !draft.scheduleAfternoon })}
          />
          <ScheduleChip
            short="N"
            full="Night"
            active={draft.scheduleNight}
            primary={primary}
            onClick={() => onChange({ scheduleNight: !draft.scheduleNight })}
          />
        </div>
      </div>

      {/* Duration */}
      <div style={cellStyle}>
        <div style={{ display: 'flex', gap: 4, width: '100%' }}>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="5"
            value={draft.durationValue}
            onChange={(e) => onChange({ durationValue: e.target.value })}
            style={{ ...inputStyle, width: 56, padding: '0 6px' }}
          />
          <select
            value={draft.durationUnit}
            onChange={(e) => onChange({ durationUnit: e.target.value })}
            style={{ ...inputStyle, flex: 1, padding: '0 4px' }}
          >
            {DURATION_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Instructions */}
      <div style={cellStyle}>
        <input
          type="text"
          placeholder="e.g. Take in lukewarm water"
          value={draft.instructions}
          onChange={(e) => onChange({ instructions: e.target.value })}
          style={inputStyle}
        />
      </div>

      {/* Actions */}
      <div
        style={{
          ...cellStyle,
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        {dirty && (
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            title={draft.isNew ? 'Save item' : 'Update item'}
            style={{
              height: 30,
              width: 30,
              padding: 0,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: !canSave ? 'var(--text-disabled)' : primary,
              color: '#fff',
              cursor: !canSave ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NavIcon name={saving ? 'loader' : 'check'} />
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          title="Remove item"
          style={{
            height: 30,
            width: 30,
            padding: 0,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <NavIcon name="x" />
        </button>
      </div>
    </>
  );
}

// ─── Schedule chip ───

function ScheduleChip({
  short,
  full,
  active,
  primary,
  onClick,
}: {
  short: string;
  full: string;
  active: boolean;
  primary: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={full}
      aria-label={full}
      style={{
        width: 34,
        height: 34,
        padding: 0,
        borderRadius: 'var(--radius-sm)',
        border: `1px solid ${active ? primary : 'var(--border)'}`,
        background: active ? primary : 'var(--bg-surface)',
        color: active ? '#fff' : 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {short}
    </button>
  );
}

// ─── Item Name Picker (combobox: free text + Medicine inventory dropdown) ───

/**
 * Inline combobox that lets the prescriber either type a medicine name
 * (free-text mode) or pick a Medicine item from inventory. Selection
 * triggers `onSelectInventory` with the picker selection; pure typing
 * triggers `onTypeFreeText`. When bound to an inventory item, an "×"
 * chip lets the prescriber clear the binding.
 *
 * Backed by `qInventoryPickerCandidates` (PRD.188). Falls back gracefully
 * if the inventory query is empty — free text always remains available.
 */
function ItemNamePicker({
  value,
  inventoryItemId,
  onTypeFreeText,
  onSelectInventory,
  onClearInventory,
  inputStyle,
  primary,
}: {
  value: string;
  inventoryItemId: string | null;
  onTypeFreeText: (text: string) => void;
  onSelectInventory: (selection: InventoryPickerSelection) => void;
  onClearInventory: () => void;
  inputStyle: React.CSSProperties;
  primary: string;
}) {
  const [open, setOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  // PRD.198 fix — the dropdown is portaled to document.body so it escapes
  // the prescription grid's `overflowX: auto` clipping context. Position
  // is recomputed from the anchor's bounding rect whenever the dropdown
  // opens or the page scrolls/resizes.
  const [anchorRect, setAnchorRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  // Close on outside click — also dismiss when click lands on the
  // portaled dropdown's container outside both anchor and dropdown.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      // The portaled dropdown lives outside containerRef, so check it
      // separately by walking up looking for our role=listbox marker.
      let el: Node | null = target;
      while (el && el !== document.body) {
        if (el instanceof HTMLElement && el.dataset?.pickerDropdown === 'item-name') {
          return;
        }
        el = el.parentNode;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Track the anchor's screen position while the dropdown is open. We
  // use a layout effect so the dropdown paints in the right place on
  // open without a flicker, and listen for scroll/resize so it follows
  // the input if the user scrolls the encounter view.
  useLayoutEffect(() => {
    if (!open) {
      setAnchorRect(null);
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // PRD.205 — dermatology prescriptions/care plans need Product results
  // alongside Medicine. The picker query now accepts an itemTypes filter;
  // results are split into Medicine and Product sections below so the
  // prescriber can tell them apart.
  const { data: candidateRows = [] } = usePowerSyncQuery<InventoryPickerCandidateRow>(
    qInventoryPickerCandidates(debouncedQuery, ['medicine', 'product']),
  );
  const medicineCandidateRows = useMemo(
    () => candidateRows.filter((r) => r.item_type !== 'product'),
    [candidateRows],
  );
  const productCandidateRows = useMemo(
    () => candidateRows.filter((r) => r.item_type === 'product'),
    [candidateRows],
  );

  // PRD.198 — Manual fallback row appears when the user has typed something
  // that doesn't exactly match an inventory result. Click it to dismiss the
  // dropdown; the typed text is already in the draft via onTypeFreeText.
  const trimmed = value.trim();
  const hasTyped = trimmed.length > 0;
  const exactInventoryMatch = candidateRows.some(
    (r) => (r.item_name ?? '').toLowerCase() === trimmed.toLowerCase(),
  );
  const showMedicineSection = medicineCandidateRows.length > 0;
  const showProductSection = productCandidateRows.length > 0;
  const showInventorySection = candidateRows.length > 0;
  const showManualOption = hasTyped && !exactInventoryMatch;
  const showDropdown = open && (showInventorySection || showManualOption);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div ref={anchorRef} style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
        <input
          type="text"
          placeholder="Search medicine, product, or type custom item…"
          value={value}
          onChange={(e) => {
            onTypeFreeText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          style={inputStyle}
        />
        {inventoryItemId && (
          <span
            title="Linked to inventory item — click × to switch to free text"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              padding: '0 6px',
              height: 22,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success-bg, #dcfce7)',
              color: 'var(--color-success-text, #166534)',
              fontSize: 10,
              fontWeight: 600,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            INV
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClearInventory();
              }}
              aria-label="Unlink inventory item"
              style={{
                marginLeft: 2,
                width: 14,
                height: 14,
                padding: 0,
                borderRadius: 'var(--radius-full)',
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </span>
        )}
      </div>

      {showDropdown &&
        anchorRect &&
        createPortal(
          <div
            role="listbox"
            data-picker-dropdown="item-name"
            style={{
              position: 'fixed',
              top: anchorRect.top + 4,
              left: anchorRect.left,
              width: anchorRect.width,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              maxHeight: 320,
              overflowY: 'auto',
              zIndex: 1000,
            }}
          >
            {showMedicineSection && <DropdownSectionLabel>Inventory Medicine</DropdownSectionLabel>}
            {showMedicineSection &&
              medicineCandidateRows.slice(0, 20).map((row) => (
                <PickerOptionRow
                  key={row.id}
                  row={row}
                  badge="inventory"
                  onSelect={(sel) => {
                    onSelectInventory(sel);
                    setOpen(false);
                  }}
                />
              ))}
            {/* PRD.205 — Inventory Product results in dermatology
                prescription/care-plan search. Same write path as Medicine
                (inventory_item_id + snapshots), no stock movement, no
                Pharmacy queue entry. */}
            {showProductSection && <DropdownSectionLabel>Inventory Product</DropdownSectionLabel>}
            {showProductSection &&
              productCandidateRows.slice(0, 20).map((row) => (
                <PickerOptionRow
                  key={row.id}
                  row={row}
                  badge="product"
                  onSelect={(sel) => {
                    onSelectInventory(sel);
                    setOpen(false);
                  }}
                />
              ))}
            {showManualOption && <DropdownSectionLabel>Manual</DropdownSectionLabel>}
            {showManualOption && (
              <button
                type="button"
                role="option"
                onClick={() => setOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    'var(--bg-hover, rgba(0,0,0,0.04))';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <ResultBadge kind="manual" primary={primary} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--text)',
                    }}
                  >
                    Use “{trimmed}” as custom medicine
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      marginTop: 1,
                    }}
                  >
                    Saved as free-text medicine — not linked to inventory.
                  </div>
                </div>
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

function DropdownSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '6px 10px 4px',
        fontSize: 10,
        fontWeight: 'var(--font-weight-semi)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted)',
        background: 'var(--bg-surface-2, #fafafa)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  );
}

// PRD.205 — extracted dropdown row so Medicine and Product sections share
// identical visual treatment with only the badge differing. Rendered for
// each candidate row in the prescription/care-plan unified search.
function PickerOptionRow({
  row,
  badge,
  onSelect,
}: {
  row: InventoryPickerCandidateRow;
  badge: 'inventory' | 'product';
  onSelect: (selection: InventoryPickerSelection) => void;
}) {
  const selection = pickerCandidateRowToSelection(row);
  const subtitleParts = [
    selection.genericName && `Generic: ${selection.genericName}`,
    selection.brandName && `Brand: ${selection.brandName}`,
    selection.sku && `SKU: ${selection.sku}`,
  ].filter(Boolean);
  const stockHint =
    selection.trackStock && selection.currentStockDisplay != null
      ? `${selection.currentStockDisplay} ${selection.inventoryUnit ?? ''}`.trim()
      : null;
  return (
    <button
      type="button"
      role="option"
      onClick={() => onSelect(selection)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        background: 'transparent',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'var(--bg-hover, rgba(0,0,0,0.04))';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <ResultBadge kind={badge} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {selection.itemName}
        </div>
        {subtitleParts.length > 0 && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 1,
            }}
          >
            {subtitleParts.join(' · ')}
          </div>
        )}
        {(selection.dosageForm || selection.route || stockHint) && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 1,
            }}
          >
            {[selection.dosageForm, selection.route, stockHint && `Stock: ${stockHint}`]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>
    </button>
  );
}

function ResultBadge({
  kind,
  primary,
}: {
  // PRD.205 — `product` joins Medicine and Manual as a labeled result type
  // for dermatology prescription search.
  kind: 'inventory' | 'product' | 'manual';
  primary?: string;
}) {
  const palette =
    kind === 'inventory'
      ? {
          bg: 'var(--color-info-bg, #dbeafe)',
          color: 'var(--color-info-text, #1e40af)',
          label: 'Medicine',
        }
      : kind === 'product'
        ? { bg: '#eef2ff', color: '#4338ca', label: 'Product' }
        : {
            bg: 'var(--color-neutral-bg, #f1f5f9)',
            color: primary || 'var(--text-muted)',
            label: 'Manual',
          };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 6px',
        height: 18,
        borderRadius: 'var(--radius-full)',
        background: palette.bg,
        color: palette.color,
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        flexShrink: 0,
        marginTop: 2,
      }}
    >
      {palette.label}
    </span>
  );
}
